import React, { useEffect, useState, useRef } from 'react';

// KPI color mapping
const kpiBg = {
  green: 'bg-[#c5e5dc]',
  yellow: 'bg-[#f5eac4]',
  orange: 'bg-[#f5eac4]',
  red: 'bg-[#ffa4a7]',
  none: '',
};

// Highlight color mapping
const highlightBg = {
  yellow: 'bg-[#f5eac4]',
  green: 'bg-[#c5e5dc]',
  red: 'bg-[#ffa4a7]',
  orange: 'bg-[#ffa4a7]',
  none: '',
};

type HighlightType = 'green' | 'yellow' | 'orange' | 'red' | 'none';

interface StatMatrixField {
  label: string;
  key: string;
  themKey: string;
  highlight?: HighlightType;
}

interface TeamsAnalyticsTableProps {
  fields: StatMatrixField[];
  columns: any[];
  kpiEnabled?: boolean;
}

const TeamsAnalyticsTable: React.FC<TeamsAnalyticsTableProps> = ({ fields, columns, kpiEnabled = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const periodsCols = columns.filter((c) => c.label !== 'Totals' && c.label !== 'Per Game');
  const totalsCols = columns.filter((c) => c.label === 'Totals');
  const perGameCols = columns.filter((c) => c.label === 'Per Game');

  // Dynamic width calculations
  const cellHeight = 'h-[36px] md:h-[42px]';
  const baseCategoryMinWidth = isMobile ? 128 : 160;
  const spacingWidth = 32; // Approximate spacing between tables (w-2 md:w-4 + w-4 md:w-8)

  // Calculate total number of value cells across all tables
  const totalValueCells = (periodsCols.length + totalsCols.length + perGameCols.length) * 2; // *2 for Us/Them

  // First, calculate preliminary cell width (use floor, minimum 64px)
  const preliminaryCellWidth =
    totalValueCells > 0
      ? Math.max(64, Math.floor((containerWidth - baseCategoryMinWidth - spacingWidth) / totalValueCells))
      : 64;

  // Category width should be at least 2x the value cell width for good ratio
  const categoryMinWidth = Math.max(baseCategoryMinWidth, Math.floor(preliminaryCellWidth * 2));

  // Recalculate final cell width with adjusted category width (use floor, minimum 64px)
  const availableWidth = containerWidth - categoryMinWidth - spacingWidth - 50;
  const dynamicCellWidth = totalValueCells > 0 ? Math.max(64, Math.floor(availableWidth / totalValueCells)) : 64;

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
      setIsMobile(window.innerWidth < 768);
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const renderTable = (cols: any[], keySuffix: string, showRowLabels: boolean) => (
    <div
      className={`bg-white flex flex-col ${
        keySuffix === 'periods' ? 'rounded-l-md' : keySuffix === 'pergame' ? 'rounded-r-md' : ''
      } shadow-[0_4px_8px_rgba(0,0,0,0.20),_0_-4px_8px_rgba(0,0,0,0.20)]`}
    >
      <table
        className="border-separate border-spacing-0 text-center text-[11px] md:text-base w-full"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          {showRowLabels && <col style={{ width: `${categoryMinWidth}px` }} />}
          {cols.map((col: any) => [
            <col key={col.label + '-us-' + keySuffix} style={{ width: `${dynamicCellWidth}px` }} />,
            <col key={col.label + '-them-' + keySuffix} style={{ width: `${dynamicCellWidth}px` }} />,
          ])}
        </colgroup>
        <thead>
          <tr>
            {showRowLabels && (
              <th
                className={`border border-sd-line bg-sd-navy text-white text-left py-2 text-[12px] md:text-base font-semibold sticky left-0 z-20 p-2 ${cellHeight}`}
                style={{
                  minWidth: `${categoryMinWidth}px`,
                  backgroundColor: 'rgb(var(--sd-navy))',
                }}
              ></th>
            )}
            {cols.map((col: any) => (
              <th
                key={col.label + '-header-' + keySuffix}
                colSpan={2}
                className={`border border-sd-line bg-sd-navy text-white text-[12px] md:text-base font-semibold py-2 ${cellHeight}`}
                style={{ width: `${dynamicCellWidth * 2}px` }}
              >
                {col.label}
              </th>
            ))}
          </tr>
          <tr>
            {showRowLabels && (
              <th
                className={`border bg-white h-[25px] md:h-[38px] border-sd-line text-white sticky left-0 z-20`}
                style={{ backgroundColor: '#ffffff' }}
              ></th>
            )}
            {cols.map((col: any) => [
              <th
                key={col.label + '-us-sub-' + keySuffix}
                className={`border border-sd-line bg-white h-[25px] md:h-[38px] text-sd-navy text-[10px] md:text-sm font-semibold p-1 md:p-2 whitespace-nowrap`}
                style={{ width: `${dynamicCellWidth}px` }}
              >
                Us
              </th>,
              <th
                key={col.label + '-them-sub-' + keySuffix}
                className={`border border-sd-line bg-white h-[25px] md:h-[38px] text-sd-navy text-[10px] md:text-sm font-semibold p-1 md:p-2 whitespace-nowrap`}
                style={{ width: `${dynamicCellWidth}px` }}
              >
                Them
              </th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {fields.map((row, rowIdx) => {
            const rowBgClass = rowIdx % 2 === 0 ? 'bg-white' : 'bg-sd-light';
            return (
              <tr key={row.label + '-' + keySuffix}>
                {showRowLabels && (
                  <td
                    className={`border border-sd-line text-left p-2 text-[12px] md:text-base font-semibold text-sd-dark sticky left-0 z-10 ${rowBgClass} ${cellHeight}`}
                    style={{
                      minWidth: `${categoryMinWidth}px`,
                      backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : 'rgb(var(--sd-light))',
                    }}
                  >
                    {row.label}
                  </td>
                )}
                {cols.map((col: any) => {
                  const kpiColor = col[row.key + 'KpiColor'] as HighlightType;
                  const usBgClass = kpiEnabled
                    ? kpiColor && kpiColor !== 'none'
                      ? kpiBg[kpiColor]
                      : row.highlight
                        ? highlightBg[row.highlight]
                        : rowBgClass
                    : rowBgClass;
                  const kpiTarget = col[row.key + 'KpiTarget'];
                  return [
                    <td
                      key={col.label + '-us-' + keySuffix}
                      className={`border border-sd-line text-sm md:text-lg font-bold cursor-default whitespace-nowrap ${cellHeight} ${usBgClass} ${
                        kpiTarget ? 'relative group' : ''
                      }`}
                      style={{ width: `${dynamicCellWidth}px` }}
                    >
                      {col[row.key]}
                      {kpiTarget && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-sm rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Target: {kpiTarget}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                      )}
                    </td>,
                    <td
                      key={col.label + '-them-' + keySuffix}
                      className={`border border-sd-line text-sm md:text-lg font-bold whitespace-nowrap ${cellHeight} ${rowBgClass}`}
                      style={{ width: `${dynamicCellWidth}px` }}
                    >
                      {col[row.themKey]}
                    </td>,
                  ];
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div ref={containerRef} className="w-full overflow-x-auto overflow-y-visible py-2">
        <div className="flex flex-row items-stretch md:px-2" style={{ width: '100%', minWidth: 'max-content' }}>
          {(() => {
            const firstKey = periodsCols.length ? 'periods' : totalsCols.length ? 'totals' : 'pergame';
            return (
              <>
                {periodsCols.length > 0 && (
                  <div
                    className="flex-none"
                    style={{
                      width: `${
                        periodsCols.length * 2 * dynamicCellWidth + (firstKey === 'periods' ? categoryMinWidth : 0)
                      }px`,
                    }}
                  >
                    {renderTable(periodsCols, 'periods', firstKey === 'periods')}
                  </div>
                )}
                {/* first spacer */}
                <div className="w-2 md:w-4 shrink-0" />
                {totalsCols.length > 0 && (
                  <div
                    className="flex-none"
                    style={{
                      width: `${
                        totalsCols.length * 2 * dynamicCellWidth + (firstKey === 'totals' ? categoryMinWidth : 0)
                      }px`,
                    }}
                  >
                    {renderTable(totalsCols, 'totals', firstKey === 'totals')}
                  </div>
                )}
                {/* second spacer (2x first) */}
                <div className="w-4 md:w-8 shrink-0" />
                {perGameCols.length > 0 && (
                  <div
                    className="flex-none"
                    style={{
                      width: `${
                        perGameCols.length * 2 * dynamicCellWidth + (firstKey === 'pergame' ? categoryMinWidth : 0)
                      }px`,
                    }}
                  >
                    {renderTable(perGameCols, 'pergame', firstKey === 'pergame')}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
};

export default TeamsAnalyticsTable;
export type { StatMatrixField, HighlightType };
